/**
 * SunderSkillEffect — Blades tree sunder path: heavy overhead strike.
 *
 * Three-branch behavior based on target defense type:
 * - Shield: bypasses shield entirely (full damage lands), no armor_shred
 * - Armored: applies armor_shred (40% reduction, 5s)
 * - Unarmored: 1.5× damage multiplier, no armor_shred
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Sunder
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export type DefenseType = 'shield' | 'armored' | 'unarmored';

export interface SunderDeps {
  readonly combat: {
    startAttack(attackType: string): number; // returns enemies hit
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
    }): void;
  };
  readonly target: {
    getDefenseType(): DefenseType;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SunderConfig {
  readonly cooldownMs: number;
  readonly armorShredIntensity: number;
  readonly armorShredDurationMs: number;
  readonly unarmoredDamageMultiplier: number;
}

const DEFAULT_CONFIG: SunderConfig = {
  cooldownMs: 1000,                   // medium cooldown tier
  armorShredIntensity: 0.4,           // 40% armor reduction
  armorShredDurationMs: 5000,         // 5s shred duration
  unarmoredDamageMultiplier: 1.5,     // 50% bonus vs unarmored
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SunderSkillEffect implements SkillEffect {
  readonly skillId = 'sunder';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SunderDeps;
  private readonly config: SunderConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _lastDefenseType: DefenseType | null = null;
  private _shieldBypassed = false;
  private _armorShredApplied = false;
  private _resolvedDamageMultiplier = 1.0;

  constructor(deps: SunderDeps, config: Partial<SunderConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get attacksFired(): number { return this._attacksFired; }
  get lastDefenseType(): DefenseType | null { return this._lastDefenseType; }
  get shieldBypassed(): boolean { return this._shieldBypassed; }
  get armorShredApplied(): boolean { return this._armorShredApplied; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Reset per-activation state
    this._shieldBypassed = false;
    this._armorShredApplied = false;
    this._resolvedDamageMultiplier = 1.0;

    // Check target defense type
    const defenseType = this.deps.target.getDefenseType();
    this._lastDefenseType = defenseType;

    // Fire the overhead strike
    const enemiesHit = this.deps.combat.startAttack('sunder');
    this._attacksFired++;

    if (enemiesHit > 0) {
      switch (defenseType) {
        case 'shield':
          // Bypass shield — full damage lands, no armor_shred
          this._shieldBypassed = true;
          break;

        case 'armored':
          // Apply armor_shred debuff
          this.deps.statusEffects.apply('armor_shred', {
            sourceSkillId: 'sunder',
            durationMs: this.config.armorShredDurationMs,
            intensity: this.config.armorShredIntensity,
          });
          this._armorShredApplied = true;
          break;

        case 'unarmored':
          // Bonus damage multiplier
          this._resolvedDamageMultiplier = this.config.unarmoredDamageMultiplier;
          break;
      }
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
